import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, utcToZonedTime } from 'date-fns-tz';

import IlustracaoDestaque from 'assets/img/destaque-ilustracao.png';
import Button from 'components/ui/button';
import ProgressBar from 'components/ui/progress-bar';

import { EstadoDoCadastro, FaseDoCiclo } from '../realizacoes-do-semestre';

const FUSO = 'America/Sao_Paulo';
const SEGUNDO = 1000;
const MINUTO = 60 * SEGUNDO;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

const doisDigitos = (valor: number) => String(valor).padStart(2, '0');

/** "dd/MM" no horário de Brasília. */
export function diaEMes(ms: number): string {
  return format(utcToZonedTime(ms, FUSO), 'dd/MM', { timeZone: FUSO });
}

/** Contagem regressiva do design: "13 dias, 06:50:00"; zera quando o prazo passa. */
export function tempoRestante(ms: number): string {
  const resto = Math.max(0, ms);
  const dias = Math.floor(resto / DIA);
  const horas = Math.floor((resto % DIA) / HORA);
  const minutos = Math.floor((resto % HORA) / MINUTO);
  const segundos = Math.floor((resto % MINUTO) / SEGUNDO);
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}, ${doisDigitos(horas)}:${doisDigitos(minutos)}:${doisDigitos(segundos)}`;
}

/** Horário do servidor andando de segundo em segundo a partir de `inicio`. */
function useRelogio(inicio: number): number {
  const [agora, setAgora] = useState(inicio);

  useEffect(() => {
    setAgora(inicio);
    const intervalo = setInterval(() => setAgora(atual => atual + SEGUNDO), SEGUNDO);
    return () => clearInterval(intervalo);
  }, [inicio]);

  return agora;
}

function Relogio() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function Alerta() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** RDN-22: prazo da fase aberta — contagem até o fim, barra do período e datas de início e fim. */
export function PrazoDaFase({ fase, agora }: { fase: FaseDoCiclo; agora: number }) {
  const relogio = useRelogio(agora);
  const duracao = fase.date_finish - fase.date_start;
  const decorrido = duracao > 0 ? ((relogio - fase.date_start) / duracao) * 100 : 100;

  return (
    <>
      <p className="m-0 mb-3 flex flex-wrap items-center gap-2 text-xs text-content-soft">
        O cadastramento desta fase encerra em
        <span className="inline-flex items-center gap-[5px] rounded-jj-pill bg-brand-yellow px-2.5 py-1 font-display text-xs font-bold text-brand-blue-dark">
          <Relogio />
          <span>{tempoRestante(fase.date_finish - relogio)}</span>
        </span>
      </p>
      <div className="flex items-center gap-2.5">
        <span className="text-[10px] text-content-soft">{diaEMes(fase.date_start)}</span>
        <ProgressBar value={decorrido} label="Período de cadastramento da fase" className="flex-1" />
        <span className="text-[10px] text-content-soft">{diaEMes(fase.date_finish)}</span>
      </div>
    </>
  );
}

export interface ChamadaDeCadastroProps {
  /** `estadoDoCadastro`; `null` enquanto o perfil ou o horário do servidor não chegaram. */
  estado: EstadoDoCadastro | null;
  /** Horário do servidor, em milissegundos. */
  agora: number;
  /** Rascunho parcial do cadastro (`get_partial_task` com `team`). */
  temRascunho: boolean;
  onIrParaCicloAtual: (cicloId: string) => void;
}

function ConteudoDoEstado({ estado, agora, temRascunho, onIrParaCicloAtual }: ChamadaDeCadastroProps & { estado: EstadoDoCadastro }) {
  const navigate = useNavigate();

  switch (estado.tipo) {
    case 'aberto-sem-realizacao':
      return (
        <>
          <PrazoDaFase fase={estado.fase} agora={agora} />
          <div className="mt-4 flex flex-wrap items-center gap-3.5">
            <div className="relative inline-flex">
              <Button variant="solido-destaque" data-abre-cadastro onClick={() => navigate('cadastrar')}>
                Cadastrar Realização Destaque
              </Button>
              <span
                role="img"
                aria-label="Você ainda não cadastrou sua Realização Destaque nesta fase"
                title="Você ainda não cadastrou sua Realização Destaque nesta fase"
                className="absolute -right-[9px] -top-[9px] flex h-6 w-6 items-center justify-center rounded-full bg-brand-yellow text-brand-blue-dark motion-safe:animate-pulse"
              >
                <Alerta />
              </span>
            </div>
            {temRascunho && (
              <span className="rounded-jj-pill bg-brand-yellow-light px-3 py-[5px] text-[11px] font-bold text-brand-blue-dark">
                Realização em preenchimento
              </span>
            )}
            <span className="text-[11px] leading-[1.5] text-content-soft">1 realização por fase · somente entregas concluídas</span>
          </div>
        </>
      );

    case 'aberto-com-realizacao':
      return (
        <>
          <PrazoDaFase fase={estado.fase} agora={agora} />
          <div className="mt-4">
            {estado.editavel ? (
              <Button
                variant="solido-destaque"
                data-abre-cadastro
                onClick={() => navigate(`/realizacao-destaque/${estado.realizacaoId}`)}
              >
                Editar minha realização
              </Button>
            ) : (
              <p className="m-0 text-[12.5px] text-content">Você já cadastrou a realização desta fase.</p>
            )}
          </div>
        </>
      );

    case 'indisponivel':
      return <PrazoDaFase fase={estado.fase} agora={agora} />;

    case 'em-outro-ciclo':
      return (
        <div className="flex flex-wrap items-center gap-3.5">
          <p className="m-0 flex-[1_1_240px] text-[12.5px] leading-[1.55] text-content">
            O cadastramento está aberto no{' '}
            <b>
              Ciclo {estado.ciclo.title} · {estado.fase.title}
            </b>
            .
          </p>
          <Button variant="solido-destaque" onClick={() => onIrParaCicloAtual(estado.ciclo._id)}>
            Ir para o ciclo atual
          </Button>
        </div>
      );

    case 'fechado':
      return (
        <p className="m-0 text-[12.5px] leading-[1.55] text-content">
          O cadastramento está fechado.
          {estado.proximaAbertura !== undefined && ` O próximo abre em ${diaEMes(estado.proximaAbertura)}.`}
        </p>
      );
  }
}

/** Bloco verde do topo do participante (RDN-22…RDN-27): como funciona e o estado do cadastramento. */
export default function ChamadaDeCadastro(props: ChamadaDeCadastroProps) {
  const { estado } = props;

  return (
    <section
      aria-labelledby="titulo-como-funciona"
      className="grid grid-cols-1 items-center gap-6 rounded-jj-panel bg-brand-green px-[30px] py-[26px] md:grid-cols-[minmax(0,1fr)_200px]"
    >
      <div>
        <h2 id="titulo-como-funciona" className="m-0 mb-2.5 font-display text-[19px] font-bold text-brand-blue-dark">
          Como funciona a dinâmica Realização Destaque?
        </h2>
        <p className="m-0 mb-2.5 text-[13px] leading-[1.65] text-brand-navy">
          Essa dinâmica valoriza a contribuição dos funcionários para os objetivos do Banco, com apreciação e reconhecimento
          das entregas pela liderança das unidades.
        </p>
        <p className="m-0 mb-4 text-[13px] leading-[1.65] text-brand-navy">
          Cadastre sua realização mais relevante do mês. Ela será apreciada pelos seus líderes e todos os participantes
          poderão receber pontos, <em>feedbacks</em> e <b>Selo Realização Destaque</b>.
        </p>

        {estado && (
          <div className="rounded-jj-lg bg-brand-white px-[18px] py-4">
            <ConteudoDoEstado {...props} estado={estado} />
          </div>
        )}
      </div>
      <img src={IlustracaoDestaque} alt="" className="hidden h-auto w-full md:block" />
    </section>
  );
}
